import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { api, ApiError } from '../api/client'
import type { ConsoleDashboard, DashboardKind, DashboardWidget, EagleOverview, EagleWallMe } from '../api/types'
import { usePermessiModuli } from '../auth/permessi'
import { Griglia, RIGHE_SCHERMO, SinotticoContext } from '../components/Griglia'
import { useRiquadro } from '../components/widget/graficiEagle'
import Marchio from '../components/Marchio'
import { Orologio } from '../components/Orologio'
import { DisegnaWidget, type DefinizioneWidget } from '../components/widget/catalogo'
import { dashboardPredefinitaEagle, dashboardPredefinitaSinottico } from '../components/widget/modello'
import { useAsync, usePolling } from '../hooks/useAsync'
import { useEagle } from '../hooks/useEagle'
import { t } from '../i18n/lingua'

/**
 * Il sinottico di Eagle: il monitoraggio su un monitor a parete.
 *
 * In alto, a sinistra il marchio (CENSO · DiscoveryGuard), al centro i numeri
 * (gravi, attenzione, ok, CI, sonde), a destra l'orologio e «Eagle · titolo»
 * — il titolo è il nome della dashboard, o quello scritto sul link del
 * monitor. Sotto, la **dashboard del sinottico** (`kind: eagle_wall`): i
 * widget di Eagle scelti e ridimensionati da Eagle → Sinottico con lo stesso
 * motore del cruscotto. Più sezioni ruotano al ritmo scelto (frecce per
 * cambiare a mano, spazio per fermare); Esc esce.
 *
 * Si apre in due modi. Dalla console (`/eagle/sinottico`), con la sessione
 * di chi guarda: usa la dashboard indicata (`?d=`), altrimenti l'ultima
 * aperta, quella del tenant, la personale, o la predefinita. Oppure da un
 * **link per i monitor** (`/monitor/eagle/<token>`, `chiosco`): nessuna
 * sessione, il token nell'indirizzo vale solo per le letture del sinottico
 * e solo dagli indirizzi autorizzati; mostra la dashboard che il link
 * indica, senza «Personalizza» né «Esci».
 */
/** Il nome del modulo: un nome di prodotto, uguale in ogni lingua. */
const NOME = 'Eagle'

export default function EagleSinottico({ chiosco = false }: { chiosco?: boolean }) {
  // A wall screen has no theme switch: CENSO Monitor puts the monitor's
  // setting in the address (?theme=dark|light), applied here on the document
  // alone, never stored, so a normal browser keeps its own choice.
  useEffect(() => {
    if (!chiosco) return
    const tema = new URLSearchParams(window.location.search).get('theme')
    if (tema === 'dark' || tema === 'light') document.documentElement.dataset.theme = tema
    else if (tema === 'auto') delete document.documentElement.dataset.theme
  }, [chiosco])
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const permessi = usePermessiModuli()
  const eagle = useEagle()
  const dati = usePolling(() => api.eagleOverview(6), 30_000)
  // What goes on the wall: a wall display (`kind=eagle_wall`, the default) or
  // a cockpit (`kind=eagle`) — «Mostra a parete» from the cockpit shows the
  // cockpit itself, not something composed elsewhere. Through the link: what
  // the link says to show.
  // What goes on the wall is a cockpit. `kind=eagle_wall` is what links made
  // before migration 0217 say: those dashboards are cockpits now.
  const kind: DashboardKind = 'eagle'
  const elenco = useAsync<EagleWallMe | { dashboards: ConsoleDashboard[] }>(
    () => (chiosco ? api.wallLinkMe() : api.dashboards(kind)), [chiosco, kind])
  // Through the link, a refusal is retried every minute: the site's address
  // may have just changed (the link follows it at the next heartbeat), or
  // the link was renewed a moment ago. A wall has nobody to press F5.
  useEffect(() => {
    if (!chiosco || !elenco.error) return
    const id = window.setTimeout(() => elenco.reload(), 60_000)
    return () => window.clearTimeout(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chiosco, elenco.error])
  // `sezione=N`: start where the cockpit was, not at the first section.
  const [sezione, setSezione] = useState(() => Math.max(0, Number(params.get('sezione')) || 0))
  // The area under the header: a free-layout section fills it with 12 rows.
  const [area, , altezzaArea] = useRiquadro<HTMLDivElement>(0, 0)
  const [fermo, setFermo] = useState(false)
  // Full screen (or a monitor through its link): the controls — section
  // pills, Ferma, Personalizza, Schermo intero, Esci — hide after a few
  // seconds without the mouse, and come back at the first movement. The
  // clock and «Eagle · titolo» stay.
  const [pieno, setPieno] = useState(() => typeof document !== 'undefined' && !!document.fullscreenElement)
  const [comandi, setComandi] = useState(true)
  useEffect(() => {
    const onFull = () => setPieno(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', onFull)
    return () => document.removeEventListener('fullscreenchange', onFull)
  }, [])
  useEffect(() => {
    if (!pieno && !chiosco) { setComandi(true); return }
    let timer = window.setTimeout(() => setComandi(false), 4000)
    const sveglia = () => {
      setComandi(true)
      window.clearTimeout(timer)
      timer = window.setTimeout(() => setComandi(false), 4000)
    }
    const eventi = ['mousemove', 'mousedown', 'keydown', 'touchstart'] as const
    eventi.forEach((ev) => window.addEventListener(ev, sveglia, { passive: true }))
    return () => {
      window.clearTimeout(timer)
      eventi.forEach((ev) => window.removeEventListener(ev, sveglia))
    }
  }, [pieno, chiosco])

  // A monitor has no session: every Eagle widget is visible, and the server
  // decides what the link may read.
  const visibile = useMemo(() => chiosco
    ? (d: DefinizioneWidget) => !!d.eagle || !d.modulo
    : (d: DefinizioneWidget) => (!d.modulo || permessi.vede(d.modulo)) && (!d.eagle || eagle), [chiosco, permessi, eagle])
  // The default wall, built once per «who can see what»: built on every
  // render it would carry new widget ids each time, and every widget would
  // remount and reload — the blinking wall.
  // The default: on a monitor with nothing assigned, the wall layout (the
  // wall of CIs in pages); in the console, the cockpit's own default.
  const predefinita = useMemo(() => (chiosco ? dashboardPredefinitaSinottico(visibile) : dashboardPredefinitaEagle(visibile)), [visibile, chiosco])
  const dash: ConsoleDashboard | null = useMemo(() => {
    if (!elenco.data) return null
    if ('dashboard' in elenco.data) return elenco.data.dashboard ?? predefinita
    const mie = elenco.data.dashboards
    const voluta = params.get('d')
    // `bozza=1`: the dashboard on the cockpit's screen had no id (the default
    // one, or not yet saved): «Mostra a parete» left a copy for us.
    if (params.get('bozza') === '1') {
      try {
        const grezza = localStorage.getItem(`censo.dashboard.bozza.${kind}`)
        const bozza = grezza ? (JSON.parse(grezza) as ConsoleDashboard) : null
        if (bozza && Array.isArray(bozza.sections) && bozza.sections.length > 0) return bozza
      } catch { /* una bozza illeggibile: si va avanti con le altre scelte */ }
    }
    let ricordata: string | null = null
    try { ricordata = localStorage.getItem(`censo.dashboard.${kind}`) } catch { ricordata = null }
    const scelta = mie.find((d) => d.id === voluta) ?? mie.find((d) => d.id === ricordata)
      ?? mie.find((d) => d.shared) ?? mie.find((d) => !d.shared) ?? predefinita
    // Remember what the wall shows: a reload without the `?d=` in the
    // address (a bookmark, a kiosk) must come back to the same dashboard,
    // not to the default one.
    if (scelta.id && scelta.id !== ricordata) {
      try { localStorage.setItem(`censo.dashboard.${kind}`, scelta.id) } catch { /* memoria del browser non disponibile */ }
    }
    return scelta
  }, [elenco.data, params, predefinita, kind])
  // The title next to «Eagle»: the link's own, or the dashboard's name.
  const titolo: string = (elenco.data && 'title' in elenco.data ? elenco.data.title : null) || dash?.name || ''

  useEffect(() => {
    document.body.classList.add('sinottico-attivo')
    return () => document.body.classList.remove('sinottico-attivo')
  }, [])
  useEffect(() => {
    if (!titolo) return
    const prima = document.title
    document.title = `${NOME} · ${titolo}`
    return () => { document.title = prima }
  }, [titolo])
  useEffect(() => {
    if (!dash || fermo || dash.rotation_s <= 0 || dash.sections.length < 2) return
    const id = window.setInterval(() => setSezione((s) => (s + 1) % dash.sections.length), dash.rotation_s * 1000)
    return () => window.clearInterval(id)
  }, [dash, fermo])
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !chiosco) navigate('/eagle')
      if (!dash || dash.sections.length < 2) return
      if (e.key === 'ArrowRight') setSezione((s) => (s + 1) % dash.sections.length)
      if (e.key === 'ArrowLeft') setSezione((s) => (s - 1 + dash.sections.length) % dash.sections.length)
      if (e.key === ' ') { e.preventDefault(); setFermo((f) => !f) }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [dash, navigate, chiosco])

  const o: EagleOverview | null = dati.data && dati.data.licensed ? dati.data : null
  const s = o?.totals.by_status
  const gravi = s ? s.crit + s.down : 0
  const marchio = (
    // The brand, top left: a wall monitor says whose it is. Brand names do
    // not go through t(): a logo is not translated.
    <div className="esin__brand" aria-label="CENSO · DiscoveryGuard">
      <Marchio size={34} />
      <span className="esin__brand-nome" aria-hidden="true">{'CENSO'}<small>{'DiscoveryGuard'}</small></span>
    </div>
  )
  if (chiosco && elenco.error) {
    // The link does not open: say why, in the server's words (revoked,
    // expired, not from this address), with nothing else on the screen.
    const errore = elenco.error instanceof ApiError ? elenco.error.message : t('Control Plane non raggiungibile')
    return (
      <div className="sinottico esin">
        <header className="esin__testa">{marchio}</header>
        <div className="esin__avviso">
          <h2>{t('Questo monitor non può aprire il sinottico')}</h2>
          <p>{errore}</p>
          <p className="muted">{t('Il link si gestisce dalla console, in Eagle → Sinottici pubblicati → «Link per i monitor».')}</p>
        </div>
      </div>
    )
  }
  if (!dash) return null
  const sez = dash.sections[sezione % dash.sections.length]
  const ruota = dash.sections.length > 1

  return (
    <div className="sinottico esin">
      <header className="esin__testa esin__testa--righe">
        {/* The top line: the brand on the left, «Eagle · titolo» on the right,
            with all the room it wants — the controls live on the line below. */}
        <div className="esin__riga esin__riga--cima">
          {marchio}
          <div className="esin__eagle" title={titolo}>
            <span className="esin__nome">{NOME}</span>
            <span className="esin__titolo">{titolo}{ruota && <small> · {t(sez.title)}</small>}</span>
          </div>
        </div>
        <div className="esin__riga">
        {s && (
          <div className="esin__numeri">
            <div className={`esin__numero esin__numero--${gravi > 0 ? 'error' : 'ok'}`}>
              <strong>{gravi}</strong><span>{t('gravi')}</span>
            </div>
            <div className={`esin__numero esin__numero--${s.warn > 0 ? 'warn' : 'muted'}`}>
              <strong>{s.warn}</strong><span>{t('attenzione')}</span>
            </div>
            <div className="esin__numero esin__numero--ok">
              <strong>{s.ok}</strong><span>{t('ok')}</span>
            </div>
            <div className="esin__numero esin__numero--muted">
              <strong>{o!.totals.cis}</strong><span>{t('CI monitorati')}</span>
            </div>
            <div className={`esin__numero esin__numero--${o!.probes.alive < o!.probes.total ? 'warn' : 'muted'}`}>
              <strong>{o!.probes.alive}/{o!.probes.total}</strong><span>{t('sonde')}</span>
            </div>
          </div>
        )}
        <div className="esin__destra">
          <div className={`esin__comandi${comandi ? '' : ' esin__comandi--nascosti'}`} aria-hidden={!comandi}>
          {ruota && (
            <div className="dashboard__sezioni" role="tablist">
              {dash.sections.map((sz, i) => (
                <button key={sz.id} type="button" role="tab" aria-selected={i === sezione % dash.sections.length}
                        className={`dashboard__sezione${i === sezione % dash.sections.length ? ' dashboard__sezione--on' : ''}`}
                        onClick={() => setSezione(i)}>{t(sz.title)}</button>
              ))}
            </div>
          )}
          </div>
          <Orologio />
          <div className={`esin__comandi${comandi ? '' : ' esin__comandi--nascosti'}`} aria-hidden={!comandi}>
          {ruota && dash.rotation_s > 0 && (
            <button type="button" className="secondary" onClick={() => setFermo(!fermo)} title={t('Spazio per fermare o riprendere la rotazione')}>{fermo ? t('Riprendi') : t('Ferma')}</button>
          )}
          {!chiosco && (
            <button type="button" className="secondary"
                    onClick={() => navigate(`${kind === 'eagle' ? '/eagle' : '/eagle/parete'}${dash?.id ? `?d=${dash.id}` : ''}`)}
                    title={t('Scegli e ridimensiona i widget del sinottico')}>{t('Personalizza')}</button>
          )}
          <button type="button" className="secondary" onClick={() => { if (document.fullscreenElement) void document.exitFullscreen?.(); else void document.documentElement.requestFullscreen?.() }}>{t('Schermo intero')}</button>
          {!chiosco && (
            <button type="button" className="secondary" onClick={() => navigate('/eagle')} title={t('Esc per uscire')}>{t('Esci')}</button>
          )}
          </div>
        </div>
        </div>
      </header>

      <div ref={area} className="sinottico__area">
      {dati.data && !dati.data.licensed ? (
        <p className="muted">{t('Questa funzione non è attiva per il tuo spazio.')}</p>
      ) : sez.widgets.length === 0 ? (
        <p className="muted">{chiosco ? t('Il sinottico è vuoto: si compone dalla console, in Eagle → Sinottico.') : t('Il sinottico è vuoto: scegli i widget da «Personalizza».')}</p>
      ) : (
        <SinotticoContext.Provider value>
        <Griglia<DashboardWidget> widgets={sez.widgets} modifica={false} onCambia={() => undefined}
                 libero={sez.layout === 'free'} altezzaRiga={sez.layout === 'free' && altezzaArea > 0 ? Math.floor((altezzaArea - 14 * (RIGHE_SCHERMO - 1)) / RIGHE_SCHERMO) : undefined}
                 disegna={(w) => <DisegnaWidget tipo={w.type} options={w.options ?? {}} sinottico />} />
        </SinotticoContext.Provider>
      )}
      </div>
    </div>
  )
}
